import {
  Component,
  computed,
  inject,
  Signal,
  Input,
  signal,
  viewChild,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { AuthStore } from 'projects/shared/src/lib/store/auth.store';
import { RouterCommonPagesEnum } from 'projects/shared/src/lib/infrastructure/enums/common-pages.enum';
import { ExerciseStore } from 'projects/shared/src/lib/store/exercise.store';
import { DropdownMenuComponent } from 'projects/shared/src/ui-kit/common/components/dropdown-menu/dropdown-menu.component';
import { ENVIRONMENT_TOKEN } from 'projects/shared/src/lib/core/injection-tokens/environment.token';
import { DropdownOption } from 'projects/shared/src/lib/core/interfaces/dropdown';
import { PauseDialogComponent } from 'projects/shared/src/lib/components/pause-dialog/pause-dialog.component';
import { DialogService } from '../../core/services/dialog.service';
import { LogoutDialogComponent } from '../logout-dialog/logout-dialog.component';
import { NavigationService } from '../../core/services/navigation.service';
import { LanguageService, SupportedLanguage } from '../../core/services/language.service';

@Component({
  selector: 'app-navbar',
  imports: [
    TranslatePipe,
    FormsModule,
    RouterModule,
    DropdownMenuComponent,
    PauseDialogComponent,
    LogoutDialogComponent,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly languageService = inject(LanguageService);

  @Input() public startPageUrl?: string;
  @Input() public logoUrl?: string;
  @Input() public hideStartActionOnStartPage = false;
  @Input() public showPauseActionWithinTask = false;
  @Input() public showSettings = true;

  private readonly authStore = inject(AuthStore);
  private exerciseStore = inject(ExerciseStore);
  public dialogService = inject(DialogService);
  public navigationService = inject(NavigationService);
  private environment = inject(ENVIRONMENT_TOKEN);
  private router = inject(Router);

  public readonly userName: Signal<string | null>;
  public isAuthenticated = computed(() => this.authStore.authInfo().isAuthenticated);
  public isAnyTaskActive = computed(() => !!(this.exerciseStore.activeTask()?.id ?? false));
  public isExampleTaskActive = computed(() => this.exerciseStore.isExampleTaskActive());
  private handleBeforeUnloadBound = this.handleBeforeUnload.bind(this);
  public isInTask = computed(
    () => this.currentUrl().includes(RouterCommonPagesEnum.ExampleSum) && this.isAnyTaskActive()
  );
  public userDropdownOptions: Signal<DropdownOption[]> = computed(() => [
    { label: 'Lexipoort', icon: 'ph ph-squares-four ph-bold', url: this.environment.authorityUrl },
    { label: 'Logout', icon: 'ph ph-sign-out ph-bold', action: () => this.showLogoutDialog() },
    ...this.languageService.supportedLanguages.map((code) => ({
      label: `Languages.${code}`,
      icon: code === this.translate.currentLang() ? 'ph ph-check ph-bold' : undefined,
      action: () => this.selectLanguage(code),
    })),
  ]);

  public logoutDialog: Signal<LogoutDialogComponent | undefined> = viewChild('logoutDialog', {
    read: LogoutDialogComponent,
  });

  public currentUrl = signal('');

  public isItemVisible = computed(() => {
    const visibleRoutes = [this.userActivityUrl, this.settingsUrl, this.startPageUrl];
    return visibleRoutes.includes(this.currentUrl());
  });

  public isPauseVisible = computed(() => {
    return this.showPauseActionWithinTask && this.isInTask() && !this.isExampleTaskActive();
  });

  public isStartActionVisible = computed(() => {
    return !this.hideStartActionOnStartPage || this.currentUrl() !== this.startPageUrl;
  });

  constructor() {
    this.userName = computed(() => {
      const account = this.authStore.account();
      const authInfo = this.authStore.authInfo();

      const firstName = account?.firstName ?? authInfo?.userData?.given_name;
      const prefix = account?.prefix ?? authInfo?.userData?.middle_name;
      const lastName = account?.lastName ?? authInfo?.userData?.family_name;

      const fullName = [firstName, prefix, lastName]
        .map((part) => part?.trim())
        .filter((part): part is string => !!part)
        .join(' ');

      return fullName || account?.userName || null;
    });
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl.set(event.urlAfterRedirects);
      }
    });
  }

  ngOnInit(): void {
    window.addEventListener('beforeunload', this.handleBeforeUnloadBound);
  }

  ngOnDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnloadBound);
  }

  private selectLanguage(language: SupportedLanguage) {
    this.languageService.setLanguage(language, this.authStore.account()?.identityId);
  }

  public get settingsUrl() {
    return `/${RouterCommonPagesEnum.Settings}`;
  }

  public get userActivityUrl() {
    return `/${RouterCommonPagesEnum.UserActivity}`;
  }

  public login() {
    this.authStore.authorize();
  }

  public logout() {
    this.authStore.centralLogout();
  }

  public showLogoutDialog() {
    const logoutDialog = this.logoutDialog();
    if (logoutDialog && this.isInTask() && !this.isExampleTaskActive()) {
      logoutDialog.open();
    } else {
      this.logout();
    }
  }

  private handleBeforeUnload(event: BeforeUnloadEvent) {
    if (this.isInTask() && !this.navigationService.shouldBypass()) {
      event.preventDefault();
      event.returnValue = '';
    }
    return true;
  }
}
